import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/config`);
        setConfig(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch config:', err);
        setError('Failed to load configuration');
        // Set default config as fallback
        setConfig({
          tiers: {},
          pricing: {
            pro: {
              monthly: { amount: 7.99, currency: 'USD', interval: 'month' },
              annual: { amount: 49.99, currency: 'USD', interval: 'year' }
            },
            passes: {
              single: { amount: 0.99, currency: 'USD', quantity: 1 },
              fivePack: { amount: 2.99, currency: 'USD', quantity: 5 },
              monthly: { amount: 3.99, currency: 'USD', quantity: 10, interval: 'month' }
            }
          },
          features: {}
        });
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const value = {
    config,
    loading,
    error,
    // Helper methods for easier access
    getPricing: (type, plan) => {
      if (!config) return null;
      return config.pricing?.[type]?.[plan];
    },
    getTierRules: (tier) => {
      if (!config) return null;
      return config.tiers?.[tier];
    },
    getFeatures: (tier) => {
      if (!config) return null;
      return config.features?.[tier];
    }
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};
